/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import {
  PublicClientApplication,
  ServerResponseType,
  type Configuration,
  type AuthenticationResult,
} from '@azure/msal-browser';

// Vite resolves a missing OR empty build-time env var to '' (empty string),
// which is not nullish — so `??` would NOT fall back and MSAL would throw
// `redirect_uri_empty`. Use `||` so a blank value also falls back. Build the
// redirect URI from the current origin when nothing is configured, so it works
// across local (localhost:5000) and any deployed host without a baked-in value.
const redirectUri = import.meta.env.VITE_REDIRECT_URI || `${window.location.origin}/auth/callback`;

const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri,

    // Azure SPA redirect URIs send the auth code as a query param (?code=...),
    // not a hash fragment. MSAL defaults to FRAGMENT — override to QUERY.
    OIDCOptions: { serverResponseType: ServerResponseType.QUERY },
    // Prevent MSAL from doing a second full-page navigation after processing
    // the redirect (which would reload the app and lose the result).
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// MSAL v3: initialize() internally processes the redirect and memoizes the
// result. A second call to handleRedirectPromise() returns null because the
// code was already consumed. Chain them here so AuthCallback awaits this
// single promise and receives the actual auth result.
export const msalRedirectPromise: Promise<AuthenticationResult | null> = msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise());

export const loginRequest = { scopes: ['openid', 'profile', 'email'] };
